/**
 * DataResetCard — Super-admin only. Wipes operational data (products, comments,
 * leads, orders, events, chats, ledgers…) while preserving user accounts,
 * roles, and every configuration collection. Optionally reseeds a genuine,
 * realistic demo dataset for the super admin's workspace.
 *
 * Two safety layers:
 *   1) Preview (dry-run) — shows exactly how many docs would be wiped
 *      per collection before you commit.
 *   2) Confirm-typing — you must type `WIPE` to unlock the destructive run.
 */
import React, { useState } from "react";
import { AlertTriangle, ShieldAlert, Sparkles, Trash2, Wand2, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface PreviewResp {
  ok: boolean;
  would_delete: Record<string, number>;
  total_docs: number;
  collections_touched: number;
}

interface RunResp {
  ok: boolean;
  wiped?: Record<string, number>;
  seeded?: Record<string, any>;
}

export const DataResetCard: React.FC = () => {
  const [preview, setPreview] = useState<PreviewResp | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [running, setRunning] = useState(false);
  const [wipe, setWipe] = useState(true);
  const [reseed, setReseed] = useState(true);
  const [confirmText, setConfirmText] = useState("");
  const [lastRun, setLastRun] = useState<RunResp | null>(null);

  const canRun = confirmText.trim().toUpperCase() === "WIPE";

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/admin/data-reset/preview");
      setPreview(data);
      toast.success(`Preview: ${data.total_docs.toLocaleString()} docs across ${data.collections_touched} collections`);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const runReset = async () => {
    if (!canRun) return;
    setRunning(true);
    try {
      const { data } = await api.post("/admin/data-reset/run", { wipe, reseed, dry_run: false });
      setLastRun(data);
      toast.success(wipe && reseed
        ? "Data wiped and reseeded successfully"
        : wipe ? "Data wiped" : "Data reseeded");
      setConfirmText("");
      setPreview(null);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card data-testid="admin-data-reset-card" className="border-l-4 border-l-red-500">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-red-500" /> Data Reset & Genuine Reseed
        </CardTitle>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Destructive. Wipes operational data (products, comments, leads, orders, chats,
          ledgers, events, notifications…) while preserving user accounts, roles, and every
          config collection. Optionally seeds a realistic, believable dataset.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Step 1 — Preview */}
        <div className="rounded-lg border border-dashed border-neutral-300 dark:border-white/15 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Wand2 className="h-4 w-4 text-orange-500" />
            <div className="font-medium text-sm">Step 1 · Preview (dry run)</div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={runPreview}
            disabled={previewing}
            data-testid="admin-data-reset-preview"
          >
            {previewing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : <RefreshCw className="h-3.5 w-3.5 mr-2" />}
            Show what would be wiped
          </Button>
          {preview && (
            <div className="mt-3">
              <div className="text-sm mb-2">
                <span className="font-semibold text-red-600 dark:text-red-400">
                  {preview.total_docs.toLocaleString()}
                </span>{" "}
                docs in {preview.collections_touched} collections would be deleted.
              </div>
              <div className="max-h-52 overflow-y-auto rounded border border-neutral-200 dark:border-white/10 divide-y divide-neutral-100 dark:divide-white/5">
                {Object.entries(preview.would_delete)
                  .sort(([, a], [, b]) => (b as number) - (a as number))
                  .map(([col, count]) => (
                    <div key={col} className="flex justify-between text-xs px-3 py-1.5"
                         data-testid={`admin-data-reset-row-${col}`}>
                      <span className="text-neutral-600 dark:text-slate-300">{col}</span>
                      <span className="font-mono text-neutral-500">{(count as number).toLocaleString()}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Step 2 — Options */}
        <div className="rounded-lg border border-neutral-200 dark:border-white/10 p-3 space-y-2">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-4 w-4 text-fuchsia-500" />
            <div className="font-medium text-sm">Step 2 · Options</div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              data-testid="admin-data-reset-opt-wipe"
              checked={wipe}
              onChange={e => setWipe(e.target.checked)}
              className="h-4 w-4"
            />
            <Trash2 className="h-3.5 w-3.5 text-red-500" />
            Wipe operational data (keep users + config)
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              data-testid="admin-data-reset-opt-reseed"
              checked={reseed}
              onChange={e => setReseed(e.target.checked)}
              className="h-4 w-4"
            />
            <Sparkles className="h-3.5 w-3.5 text-fuchsia-500" />
            Reseed with genuine sample data (80 products, comments, leads, orders…)
          </label>
        </div>

        {/* Step 3 — Confirm */}
        <div className="rounded-lg border border-red-200 dark:border-red-800/40 bg-red-50/60 dark:bg-red-950/20 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <div className="font-medium text-sm text-red-800 dark:text-red-300">
              Step 3 · Type <code className="px-1 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-900 dark:text-red-100 font-mono text-xs">WIPE</code> to unlock
            </div>
          </div>
          <Input
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            placeholder="Type WIPE to confirm"
            data-testid="admin-data-reset-confirm-input"
            className="max-w-xs"
          />
          <Button
            variant="destructive"
            onClick={runReset}
            disabled={!canRun || running || (!wipe && !reseed)}
            data-testid="admin-data-reset-run"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldAlert className="h-4 w-4 mr-2" />}
            {wipe && reseed ? "Wipe + Reseed" : wipe ? "Wipe Only" : "Reseed Only"}
          </Button>
        </div>

        {lastRun && (
          <div className="rounded-lg border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50/60 dark:bg-emerald-950/20 p-3 text-xs space-y-2"
               data-testid="admin-data-reset-result">
            <div className="font-semibold text-emerald-800 dark:text-emerald-300">Last run</div>
            {lastRun.wiped && (
              <div>
                <span className="font-medium">Wiped:</span> {Object.values(lastRun.wiped).reduce((a, b) => (a as number) + (b as number), 0)} docs across {Object.keys(lastRun.wiped).length} collections
              </div>
            )}
            {lastRun.seeded && (
              <div className="space-y-0.5">
                <div className="font-medium">Seeded for {lastRun.seeded.user_email}:</div>
                {["products", "reviews", "testimonials", "comments", "leads", "orders", "complaints", "notifications", "audit_logs", "credit_ledger", "socibaba_events", "utm_visits"].map(k => {
                  const v = lastRun.seeded?.[k];
                  if (v == null) return null;
                  const display = typeof v === "object" ? JSON.stringify(v) : v;
                  return <div key={k} className="pl-3">· {k}: <span className="font-mono">{display as any}</span></div>;
                })}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DataResetCard;
